import { marked } from "marked";
import { routedLink } from "@/components/OliverBotV2/utils/oliverLinks.js";
import { sanitizeHtml } from "@/components/OliverBotV2/utils/sanitizeHtml.js";
import {
  captionCharts,
  captionFileCards,
  captionTableCards,
  citationWalker,
  markerHtml,
} from "./citations.js";
import {
  documentType,
  fileCardFailedHtml,
  fileCardHtml,
  fileCardSkeletonHtml,
} from "./fileCard.js";
import {
  ATTACHMENT_REFERENCE,
  MARKDOWN_IMAGE,
  bareAsNodes,
} from "./references.js";
import {
  escapeHtml,
  isTableMime,
  tableCardHtml,
  tableCardSkeletonHtml,
  tableCardSlots,
} from "./tableCard.js";

/**
 * Renderer for one conversation's answers. Passed to `marked.parse` rather than
 * `marked.use`, which is global and would leak into other `marked` consumers.
 */
export function createRenderer(conversationId, isRoutable) {
  const renderer = new marked.Renderer();

  renderer.table = function (header, body) {
    const tbody = body ? `<tbody>${body}</tbody>` : "";
    return `<div class="markdown-table-wrapper"><table><thead>${header}</thead>${tbody}</table></div>`;
  };

  // Stamped at render time, so the conversation id survives middle-click, Cmd-click and
  // copy/paste. Non-internal links keep the existing new-tab behaviour.
  renderer.link = function (href, title, text) {
    const link = routedLink(href || "", conversationId, { isRoutable });
    const titleAttr = title ? ` title="${title}"` : "";
    return link.internal
      ? `<a href="${link.href}"${titleAttr} data-oliver-internal="1">${text}</a>`
      : `<a href="${link.href}"${titleAttr} target="_blank" rel="noopener noreferrer">${text}</a>`;
  };

  // A chat answer has no page title: `#` and `##` would shout, so a heading starts at level 3.
  renderer.heading = function (text, level) {
    const shown = Math.max(level, 3);
    return `<h${shown}>${text}</h${shown}>\n`;
  };

  // Text, not marked's disabled <input>: done and open stay distinct with no form field.
  renderer.checkbox = (checked) => (checked ? "☑ " : "☐ ");

  renderer.image = function (href, title, text) {
    const safeHref = href || "";
    const safeAlt = text || "";
    const titleAttr = title ? ` title="${title}"` : "";
    return (
      `<span class="chart-image-wrapper">` +
      `<img src="${safeHref}" alt="${safeAlt}"${titleAttr} decoding="async" />` +
      `<span class="chart-actions">` +
      `<button type="button" class="chart-action-btn chart-expand-btn" title="Expand">` +
      `<i class="fas fa-expand"></i>` +
      `</button>` +
      `<a href="${safeHref}" download class="chart-action-btn" title="Download">` +
      `<i class="fas fa-download"></i>` +
      `</a>` +
      `</span>` +
      `</span>`
    );
  };

  return renderer;
}

marked.setOptions({
  breaks: true,
  gfm: true,
  headerIds: false,
  mangle: false,
});

/** A chart's slot while its file is still coming. */
function skeletonHtml(label) {
  const aria = label ? ` aria-label="${escapeHtml(label)}"` : "";
  return `<span class="artifact-skeleton"${aria}></span>`;
}

/** A download's slot while it is still resolving. */
function pendingHtml(label) {
  const text = label ? ` ${escapeHtml(label)}` : "";
  return `<span class="artifact-pending"><i class="fas fa-circle-notch fa-spin"></i>${text}</span>`;
}

/**
 * An artifact that could not be loaded, and the reader's next step: retry it
 * once in place, and if that fails too, ask Oliver for it again. A raw URL
 * has no artifact to re-sign, so it goes straight to the second.
 */
function failedHtml(label, { retryId = null, image = true } = {}) {
  const text = label ? ` ${escapeHtml(label)}` : "";
  const next = retryId
    ? `<button type="button" class="artifact-retry" data-artifact-retry="${retryId}">Retry</button>`
    : `Ask Oliver to ${image ? "draw" : "create"} it again.`;
  return `<span class="artifact-pending artifact-pending--failed"><i class="fas fa-exclamation-circle"></i>${text} — couldn't load. ${next}</span>`;
}

/**
 * Markdown to sanitised HTML, with citations, attachment references and held images
 * resolved from `artifacts` (see `useAnswerArtifacts`).
 */
export function renderAnswer(text, artifacts, { renderer, citations }) {
  if (!text) return "";
  const {
    resolvedAttachments,
    retriedAttachments,
    tables,
    loadedImages,
    failedImages,
  } = artifacts.state;

  let content = bareAsNodes(text);

  // Charts, resolved from either source: the saved message's attachments once the
  // turn has ended, or the ones fetched during it, since a streaming message has no
  // attachments yet and that is exactly when a chart needs resolving.
  // An unresolved reference must never reach `marked`, or it renders as
  // `<img src="attachment:2">`. Until it resolves, only the `!` knows which
  // placeholder to use — a handle has no row to ask for a MIME type.
  const cards = [];
  const slots = tableCardSlots();
  const asCard = (html) => {
    cards.push(html);
    return slots.slot(cards.length - 1);
  };
  content = content.replace(
    ATTACHMENT_REFERENCE,
    (_match, bang, label, ref) => {
      const id = artifacts.attachmentIdFor(ref);
      const resolved = id ? resolvedAttachments[id] : null;
      // Until a document resolves, its name is all there is to know it by.
      const namedDocument = bang !== "!" && documentType({ filename: label });
      if (!resolved) {
        if (bang === "!") return skeletonHtml(label);
        return namedDocument
          ? asCard(fileCardSkeletonHtml(label))
          : pendingHtml(label);
      }
      if (resolved.failed) {
        const retryId = retriedAttachments[id] ? null : id;
        if (namedDocument) return asCard(fileCardFailedHtml(label, retryId));
        return failedHtml(label, { retryId, image: bang === "!" });
      }
      if ((resolved.mimeType || "").startsWith("image/")) {
        return `![${label}](${resolved.url})`;
      }
      const name = label || resolved.filename || "download";
      const fileType = documentType(resolved);
      if (fileType) {
        return asCard(
          fileCardHtml(fileType, {
            url: resolved.url,
            filename: resolved.filename || name,
            size: resolved.size,
          })
        );
      }
      const table = tables[id];
      if (isTableMime(resolved.mimeType) && !table?.failed) {
        return asCard(
          table
            ? tableCardHtml(name, table, resolved, (tokens) =>
                (citations?.forTable(id, tokens) || []).map(markerHtml).join("")
              )
            : tableCardSkeletonHtml(name)
        );
      }
      return `[⬇︎ ${name}](${resolved.url})`;
    }
  );

  // Hold every image until its file is in cache — see `preloadContentImages`
  // for why the fetch is started there and only read here. Any other image is
  // never fetched, and the sanitiser renders it as its alt text.
  content = content.replace(MARKDOWN_IMAGE, (match, label, url) => {
    if (loadedImages[url] || !artifacts.isOliverFile(url)) return match;
    if (failedImages[url]) return failedHtml(label);
    return skeletonHtml(label);
  });

  let html;
  try {
    html = marked.parse(content, {
      renderer,
      walkTokens: citationWalker(citations),
    });
  } catch (error) {
    console.error("Markdown parsing error:", error);
    html = content;
  }
  const placed = cards.length ? slots.fill(html, cards) : html;
  return sanitizeHtml(
    captionFileCards(
      captionTableCards(captionCharts(placed, citations), citations),
      citations
    ),
    artifacts.isOliverFile
  );
}
